import { supabase } from "@/integrations/supabase/client";

/**
 * Check if the current coach has reached their client limit
 */
export async function hasReachedClientLimit(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return true; // If no user, consider limit reached

    // First check if user is a founder
    const { data: founderStatus } = await supabase.rpc('is_coach_founder', {
      coach_uuid: user.id
    });
    
    if (founderStatus) return false; // Founders have no limit

    // Get the current tier from profiles
    const { data: profile } = await supabase
      .from('profiles')
      .select('tier')
      .eq('id', user.id)
      .single();

    // If not beta tier, no limit
    if (profile?.tier !== 'Beta') return false;

    // Count clients for Beta tier
    const { count, error } = await supabase
      .from('clients')
      .select('*', { count: 'exact', head: true })
      .eq('coach_id', user.id);

    if (error) throw error;

    // Beta tier has a limit of 5 clients
    return count !== null && count >= 5;
  } catch (error) {
    console.error('Error checking client limit:', error);
    return false; // Default to no limit if there's an error
  }
}

/**
 * Check if a client has reached their program limit
 */
export async function hasClientReachedProgramLimit(clientId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return true; // If no user, consider limit reached

    // Using our database function to check founder status
    const { data: isValid, error } = await supabase.rpc('is_coach_founder', {
      coach_uuid: user.id
    });

    if (error) throw error;
    
    // If user is a founder, they have no limits
    if (isValid) {
      return false;
    }

    // Count programs for this client
    const { count, error: countError } = await supabase
      .from('programs')
      .select('*', { count: 'exact', head: true })
      .eq('client_id', clientId);

    if (countError) throw countError;

    // Beta tier has a limit of 2 programs per client
    return count !== null && count >= 2;
  } catch (error) {
    console.error('Error checking program limit:', error);
    return false; // Default to no limit if there's an error
  }
}

/**
 * Check if the current coach is a founder
 */
export async function isCoachFounder(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase.rpc('is_coach_founder', {
      coach_uuid: user.id
    });

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error('Error checking founder status:', error);
    return false;
  }
}

/**
 * Check if the current coach is on beta tier
 */
export async function isCoachBetaTier(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('profiles')
      .select('tier')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return data.tier === 'Beta';
  } catch (error) {
    console.error('Error checking beta tier status:', error);
    return false;
  }
}

/**
 * Get the current authenticated user
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

/**
 * Fetch client data by client ID
 */
export async function fetchClientData(clientId: string) {
  try {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .eq('client_id', clientId)
      .single();
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching client data:', error);
    return null;
  }
}

/**
 * Fetch programs for a specific client
 */
export async function fetchClientPrograms(clientId: string) {
  try {
    const { data, error } = await supabase
      .from('programs')
      .select('*')
      .eq('client_id', clientId);
      
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching client programs:', error);
    return [];
  }
}
